import React, { useState, useEffect, useCallback } from "react";

// Reusing the fetchApi helper - consider moving it to a shared utils file
const fetchApi = async (urlPath, options = {}) => {
    const serviceUrl = process.env.REACT_APP_BASICSERVICE;
    const url = `${serviceUrl}${urlPath}`;
    const token = localStorage.getItem("jwt");
    if (!token) throw new Error("Authentication token not found.");
    const response = await fetch(url, {
        method: "GET", headers: { "Content-Type": "application/json", "x-auth-token": token }, ...options,
    });
    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`API Error (${response.status}): ${response.statusText} - ${errorBody}`);
    }
    return response.json();
};

const ReportModal = ({ show, onClose, title, data, isLoading, error }) => {
    if (!show) return null;

    return (
        <div className="report-modal-overlay" onClick={onClose}>
            <div className="report-modal-content" onClick={e => e.stopPropagation()}>
                <button className="report-modal-close" onClick={onClose}>×</button>
                <h2>{title}</h2>
                {isLoading ? (
                    <div className="loading-cell"><div className="loading-spinner"></div>Loading report...</div>
                ) : error ? (
                    <div className="error-message">Error: {error}</div>
                ) : !data || data.length === 0 ? (
                    <p>No data available for this report.</p>
                ) : (
                    <table className="data-table" style={{ marginTop: '10px' }}>
                        <thead>
                            <tr>
                                <th className="text-left">{title.includes('Brokerage') ? 'Brokerage' : 'Vertical'}</th>
                                <th className="text-right">Prospects</th>
                                <th className="text-right">% of Total</th>
                                <th className="text-right">Active</th>
                                <th className="text-right">Binded</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={`${item.code || item.name}-${index}`}>
                                    <td className="text-left">{item.name || item.code || 'Unknown'}</td>
                                    <td className="text-right">{item.total}</td>
                                    <td className="text-right">{item.percentage}%</td>
                                    <td className="text-right">{item.active}</td>
                                    <td className="text-right">{item.binded}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};


const ProspectTables = ({ prospectStats, isLoadingStats }) => {
  const [topBrokerages, setTopBrokerages] = useState([]);
  const [topVerticals, setTopVerticals] = useState([]);

  // State for Full Report Modal
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState(''); // 'brokerage' or 'vertical'
  const [reportData, setReportData] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState(null);


  useEffect(() => {
    if (prospectStats?.stats && !isLoadingStats) {
      processTopBrokerages(prospectStats.stats);
      processTopVerticals(prospectStats.stats);
    } else {
        // Clear tables if stats are loading or unavailable
        setTopBrokerages([]);
        setTopVerticals([]);
    }
  }, [prospectStats, isLoadingStats]);


  const processTopBrokerages = (stats) => {
    if (!stats?.byBrokerage || !stats.total) {
        setTopBrokerages([]);
        return;
    }
    const sorted = [...stats.byBrokerage] // Already sorted by API
      .slice(0, 10) // Take top 10
      .map(item => ({
        name: item.brokerageCode || 'Unknown',
        count: item.total,
        percentage: stats.total > 0 ? ((item.total / stats.total) * 100).toFixed(1) : 0
      }));
    setTopBrokerages(sorted);
  };

  const processTopVerticals = (stats) => {
      if (!stats?.byVertical || !stats.total) {
           setTopVerticals([]);
           return;
      }
    const sorted = [...stats.byVertical] // Already sorted by API
      .slice(0, 10) // Take top 10
      .map(item => ({
        name: item.vertical || 'Unknown',
        count: item.total,
        percentage: stats.total > 0 ? ((item.total / stats.total) * 100).toFixed(1) : 0
      }));
    setTopVerticals(sorted);
  };


  const fetchFullReport = useCallback(async (type) => {
    setIsLoadingReport(true);
    setReportError(null);
    setReportData([]);
    setReportType(type);
    setShowReportModal(true);

    try {
        const result = await fetchApi(`/api/prospects/getProspectsReport?type=${type}`);
        setReportData(result.data);
    } catch (error) {
        console.error(`Error fetching full ${type} report:`, error);
        setReportError(error.message);
    } finally {
        setIsLoadingReport(false);
    }
  }, []);


  const renderBrokerageTable = () => (
      <div className="stats-table-container">
        <h3>Top 10 Brokerages by Prospects</h3>
        <div className="table-with-footer">
          <table className="stats-table">
            <thead><tr>
                <th className="text-left">Brokerage</th>
                <th className="text-right">Prospects</th>
                <th className="text-right">% of Total</th>
            </tr></thead>
            <tbody>
              {isLoadingStats ? (
                <tr><td colSpan="3" className="loading-cell"><div className="loading-spinner"></div></td></tr>
              ) : topBrokerages.length === 0 ? (
                <tr><td colSpan="3" className="empty-data-cell">No data.</td></tr>
              ) : (
                topBrokerages.map((broker, index) => (
                  <tr key={`broker-${index}`}>
                    <td className="text-left">{broker.name}</td>
                    <td className="text-right">{broker.count}</td>
                    <td className="text-right">{broker.percentage}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="table-footer">
            <button className="report-button" onClick={() => fetchFullReport('brokerage')} disabled={isLoadingStats}>
                Full Report
            </button>
          </div>
        </div>
      </div>
  );


  const renderVerticalTable = () => (
    <div className="stats-table-container">
      <h3>Top 10 Verticals by Prospects</h3>
      <div className="table-with-footer">
        <table className="stats-table">
          <thead><tr>
              <th className="text-left">Vertical</th>
              <th className="text-right">Prospects</th>
              <th className="text-right">% of Total</th>
          </tr></thead>
          <tbody>
            {isLoadingStats ? (
               <tr><td colSpan="3" className="loading-cell"><div className="loading-spinner"></div></td></tr>
            ) : topVerticals.length === 0 ? (
              <tr><td colSpan="3" className="empty-data-cell">No data.</td></tr>
            ) : (
              topVerticals.map((vertical, index) => (
                <tr key={`vertical-${index}`}>
                  <td className="text-left">{vertical.name}</td>
                  <td className="text-right">{vertical.count}</td>
                  <td className="text-right">{vertical.percentage}%</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
         <div className="table-footer">
            <button className="report-button" onClick={() => fetchFullReport('vertical')} disabled={isLoadingStats}>
                Full Report
            </button>
          </div>
      </div>
    </div>
  );


  return (
    <>
      <div className="prospect-tables-section">
        <div className="tables-row">
          {renderBrokerageTable()}
          {renderVerticalTable()}
        </div>
      </div>
      <ReportModal
          show={showReportModal}
          onClose={() => setShowReportModal(false)}
          title={`Full ${reportType === 'brokerage' ? 'Brokerage' : 'Vertical'} Report`}
          data={reportData}
          isLoading={isLoadingReport}
          error={reportError}
       />
    </>
  );
};

export default ProspectTables;